/**
 * FormSettingRow
 * ---------------
 * A SettingRow (label + description + toggle) bound directly to a boolean
 * field in the RHF form via `name`. No external state required.
 */

import { Box, BlockStack, InlineGrid, InlineStack, Text, Tooltip, Icon, Divider } from "@shopify/polaris";
import { InfoIcon } from "@shopify/polaris-icons";
import { ReactNode } from "react";
import { useController, useFormContext } from "react-hook-form";
import ToggleSwitch from "../ToggleSwitch/ToggleSwitch";
import { useVariantFieldDisabled } from "./context/VariantFieldContext";

export interface FormSettingRowProps {
    name: string;
    label: string;
    description?: string | ReactNode;
    tooltipContent?: ReactNode;
    disabled?: boolean;
    hideDivider?: boolean;
    /** Optional callback fired AFTER the value changes, useful for cascading effects */
    onChangeHook?: (newValue: boolean) => void;
}

export function FormSettingRow({
    name,
    label,
    description,
    tooltipContent,
    disabled = false,
    hideDivider = false,
    onChangeHook,
}: FormSettingRowProps) {
    const { control } = useFormContext();

    const { field } = useController({ control, name: name as any });

    const isAbDisabled = useVariantFieldDisabled(name);

    const handleToggle = () => {
        const next = !field.value;
        field.onChange(next);
        if (onChangeHook) onChangeHook(next);
    };

    return (
        <>
            <Box paddingBlock="400">
                <BlockStack gap="200">
                    <InlineGrid columns={{ xs: "1fr auto" }} alignItems="center" gap="400">
                        <BlockStack gap="100">
                            <InlineStack gap="100" align="start">
                                <Text as="span" variant="bodyMd" fontWeight="semibold">
                                    {label}
                                </Text>
                                {tooltipContent && (
                                    <Tooltip content={tooltipContent} width="wide">
                                        <Icon source={InfoIcon} tone="subdued" />
                                    </Tooltip>
                                )}
                            </InlineStack>
                            {description && (
                                <Text as="p" variant="bodyMd" tone="subdued">
                                    {description}
                                </Text>
                            )}
                        </BlockStack>
                        <ToggleSwitch
                            disabled={disabled || isAbDisabled}
                            checked={Boolean(field.value)}
                            setToggle={handleToggle}
                            zoom={1}
                        />
                    </InlineGrid>
                </BlockStack>
            </Box>
            {!hideDivider && <Divider />}
        </>
    );
}
